import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../core/currency.core";
import localeCore from "../../core/locale.core";
import { useShopId } from "../../core/login.core";
import shopCore from "../../core/shop.core";
import { useTranslation } from "../../core/translation.core";
import useShop from "../../hooks/useShop.hook";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { Product, ProductFormData } from "../../types/product";
import LocalePs from "../locale/localePs.core";
import { Tag } from "../reuseables/list.comp";
import DeleteProductDialog from "./deleteProductDialog.comp";
import { RetailShopRes } from "../../types/retailShop";
import { Membership } from "../../types/membership";
import { useForm } from "react-hook-form";
import { convertProductToForm } from "../../core/product.core";
import { Cats, CostSection, ImageSectiion, InventorySection, PriceSection } from "./productDetails.comp";
import { CheckedItemsState } from "../../pages/products";
import BrandName from "../brand/brandName.comp";
import PencilIcon from "../../icons/pencil.icon";
import MagnifyingIcon from "../../icons/magnifying.icon";
import TrashIcon from "../../icons/trash.icon";
import ProductTypeBadge from "./productTypeBadge.comp";
import { Warehouse } from "../../types/warehouse";

export default function ProductListRow({
  product,
  retailShops,
  memberships,
  checkedItems,
  warehouses,
  selectedProductIds,
  setSelectedProductIds,
}: {
  product: Product;
  retailShops?: RetailShopRes[];
  memberships?: Membership[];
  checkedItems?: CheckedItemsState;
  warehouses?: Warehouse[];
  selectedProductIds?: Set<string>;
  setSelectedProductIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const { selectedShop } = useShop();
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();

    if (node) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry.isIntersecting) setIsVisible(entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0.05,
          rootMargin: "200px",
        }
      );
      observerRef.current.observe(node);
    }
  }, []);

  useEffect(() => {
    if (!isVisible || hasFetched) return;

    setHasFetched(true);
    // Your react-query or API call here
  }, [isVisible, hasFetched]);

  const warehouseColumns =
    checkedItems?.inventory && warehouses
      ? warehouses.map((warehouse) => ({
          key: "inventory",
          size: "minmax(120px,1fr)",
          condition: true,
          warehouseId: warehouse.id,
        }))
      : [];

  const columns = [
    { key: "checkbox", size: "40px", condition: true },
    { key: "productType", size: "20px", conition: true },
    { key: "image", size: "70px", condition: checkedItems?.image },
    { key: "details", size: "minmax(200px,1fr)", condition: checkedItems?.name },
    { key: "sku", size: "minmax(100px,1fr)", condition: checkedItems?.sku },
    { key: "price2", size: "minmax(70px,1fr)", condition: checkedItems?.price2 },
    { key: "category", size: "minmax(100px,1fr)", condition: checkedItems?.category },
    { key: "brand", size: "minmax(150px,1fr)", condition: checkedItems?.brand },
    { key: "price", size: "minmax(200px,1fr)", condition: checkedItems?.price },
    { key: "cost", size: "minmax(200px,1fr)", condition: checkedItems?.cost },
    { key: "eshopPrice", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "discountPrice", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "retailPrices", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "membershipPrices", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    ...warehouseColumns,
    { key: "createdAt", size: "minmax(150px,1fr)", condition: checkedItems?.createdAt },
    { key: "status", size: "minmax(150px,1fr)", condition: checkedItems?.status },
    { key: "updatedAt", size: "minmax(150px,1fr)", condition: checkedItems?.updatedAt },
  ];

  const activeColumns = columns
    .filter((col) => col.condition === undefined || col.condition) // Remove unchecked columns
    .map((col) => col.size) // Extract column size
    .join(" ");

  const activeColumnsCount = columns.filter((col) => col.condition === undefined || col.condition).length; // Remove unchecked columns

  const methods = useForm<ProductFormData>();

  async function clickDelete() {
    dialogRef.current?.showModal();
  }

  useEffect(() => {
    if (product) methods.reset(convertProductToForm(product), { keepDirty: true });
  }, [product, methods]);

  if (!selectedShop) return null;

  function getPriceRange(variations: Product["variations"], defaultPrice: number, currency: string) {
    if (!variations || variations.length === 0) {
      return formatCurrency(defaultPrice, selectedShop?.defaultCurrency as any);
    }

    const prices = variations.map((variation) => variation.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return minPrice === maxPrice
      ? formatCurrency(minPrice, selectedShop?.defaultCurrency as any)
      : `${formatCurrency(minPrice, selectedShop?.defaultCurrency as any)} - ${formatCurrency(
          maxPrice,
          selectedShop?.defaultCurrency as any
        )}`;
  }

  function getEditLink() {
    if (product.productType === "recycle") return `product/recycle-edit/${product.id}`;
    if (product.productType === "variationChild") {
      return `/product/child-edit?variationId=${product.id}&parentId=${product.parentId}`;
    }
    return `product/edit/${product.id}`;
  }

  // ensure form is populated before rendering row
  if (Object.keys(methods.getValues()).length < 1) return null;

  const isSelected = selectedProductIds?.has(product.id) || false;

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!setSelectedProductIds) return;
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      if (e.target.checked) {
        newSet.add(product.id);
      } else {
        newSet.delete(product.id);
      }
      return newSet;
    });
  }

  return (
    <div
      ref={setRef}
      attr-name={localeCore.getLocaleValueDefault(product.name)}
      attr-sku={product.sku}
      attr-type="product-row"
      className={`sm:max-w-full lg:grid sm:py-2 gap-x-4 gap-y-3 px-6 py-6 text-sm dark:text-white group/edit `}
      style={{ gridTemplateColumns: window.innerWidth >= 1024 ? activeColumns : "1fr" }}
    >
      <div>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          data-product-id={product.id}
          className="w-4 h-4 rounded border-2 border-gray-400 cursor-pointer"
          id={`product-checkbox-${product.id}`}
        />
      </div>
      <ProductTypeBadge productType={product.productType} />
      {checkedItems?.image && <ImageSectiion product={product} />}
      {checkedItems?.name && (
        <div>
          <LocalePs value={product.name} />
        </div>
      )}
      {checkedItems?.sku && (
        <div>
          <Tag>{t(msg.product.sku)}</Tag>
          {product.sku}
        </div>
      )}
      {checkedItems?.price2 && (
        <div>
          {product.productType === "variationParent" && (
            <>
              <Tag>{t(msg.product.price2)}</Tag>
              <div>{getPriceRange(product.variations, product.price, selectedShop.defaultCurrency as any)}</div>
            </>
          )}
          {product.productType !== "variationParent" && (
            <>
              <Tag>{t(msg.product.price2)}</Tag>
              <div>{formatCurrency(product.price, selectedShop.defaultCurrency as any)}</div>
            </>
          )}
        </div>
      )}
      {checkedItems?.category && (
        <div className="flex gap-2">
          {product.productType !== "variationChild" && (
            <>
              <Tag>{t(msg.category.category)}</Tag>
              <Cats cats={product.categories} />
            </>
          )}
        </div>
      )}
      {checkedItems?.brand && (
        <div className="flex gap-2">
          <Tag>{t(msg.product.brand)}</Tag>
          <div>{product.brand && product.brand.id && <BrandName brandId={product.brand.id} />}</div>
        </div>
      )}
      {checkedItems?.cost && <CostSection product={product} methods={methods} />}

      {checkedItems?.price && (
        <PriceSection product={product} retailShops={retailShops} memberships={memberships} methods={methods} />
      )}

      {checkedItems?.inventory &&
        warehouses?.map((warehouse) => (
          <InventorySection
            key={warehouse.id}
            product={product}
            methods={methods}
            isVisible={isVisible}
            warehouseId={warehouse.id}
          />
        ))}

      {checkedItems?.status && (
        <div>
          <Tag>{t(msg.product.status)}</Tag>
          <p className="uppercase">{product.status}</p>
        </div>
      )}

      {checkedItems?.createdAt && (
        <div>
          <Tag>{t(msg.import.createdAt)}</Tag>
          <p>{product.createdAt}</p>
        </div>
      )}

      {checkedItems?.updatedAt && (
        <div>
          <Tag>{t(msg.import.updatedAt)}</Tag>
          <p>{product.updatedAt}</p>
        </div>
      )}

      <div
        className="flex gap-2 mt-2 md:opacity-0 md:group-hover/edit:opacity-100 transition-all"
        style={{ gridColumn: `span ${activeColumnsCount}` }}
      >
        <Link className="product-edit cursor-pointer underline leading-8" href={getEditLink()}>
          <PencilIcon className="w-5 h-5" />
        </Link>
        <ViewLink product={product} />
        <button className="product-edit cursor-pointer underline  text-red-600" type="button" onClick={clickDelete}>
          <TrashIcon className="w-5 h-5" />
        </button>
        <DeleteProductDialog dialogRef={dialogRef} productId={product.id} />
      </div>
    </div>
  );
}

function ViewLink({ product }: { product: Product }) {
  const shopId = useShopId();

  const {
    data: shop,
    isError,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["shop", shopId],
    queryFn: () => shopCore.getShop(),
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;

  return (
    <Link
      className="product-edit cursor-pointer underline leading-8"
      href={`${shop!.rootUrl}product/${product.id}`}
      target="_blank"
    >
      <MagnifyingIcon className="w-5 h-5" />
    </Link>
  );
}
